import {MIDI_FX} from 'constants/effect-types.js';
import {OSCILLATOR} from 'constants/effect-types.js';
import Arrow from './Arrow.js';
import React, {memo, useState, useCallback, useEffect} from 'react';
import { connect } from "react-redux";
import {NODE_WIDTH, NODE_HEIGHTS} from './constants.js';
import {ENVELOPE, LFO} from 'constants/mod-types.js';
import Colors from 'components/Colors.js';
import {
    INPUT,
    WIDTHS,
    OUTPUT} from './constants.js';
const colors = new Colors();

window.dist = 4;
window.x = 1;
window.tension = 0.4;
window.baseColor = "#2653d173";

const dist = (x1, y1, x2, y2) => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

function Edge({
    width,
    rack,
    effect,
    edge,
    setSelectedEdge,
    selected,
    modulator,
    macro,
    startDrawingEdge,
    playing,
    triggerCount,
    avgTranspose
}) {
    const [isHovered, setIsHovered] = useState(false);
    const startDraggingStart = useCallback(() => {
        rack.deleteEdge(edge);
        let x = edge.effect.x + 8;
        startDrawingEdge(edge.effect, INPUT, x, edge.effect.y, 0);
    }, [edge]);

    const startDraggingEnd = useCallback(() => {
        rack.deleteEdge(edge);
        startDrawingEdge(
            edge.fromNode,
            OUTPUT,
            edge.fromNode.x + 8 + edge.outputNumber*8,
            edge.fromNode.y + NODE_HEIGHTS[rack.getEffectType()],
            0);
    }, [edge]);

    let {x1, y1, x2, y2} = getModulatorPositions(rack, modulator, edge);
    if (macro) {
        let pos = getMacroPositions(rack, macro, edge.parameter);
        x1 = pos.x1;
        y1 = pos.y1;
        x2 = pos.x2;
        y2 = pos.y2;
    } else if (!modulator) {
        x1 = effect.x + 8 + edge.outputNumber * 8;
        y1 = effect.y + NODE_HEIGHTS[rack.getEffectType()];
        x2 = edge.effect.x + 8;
        y2 = edge.effect.y;
    }

    let className = "edge ";

    if (modulator) {
        if (modulator.getType() === ENVELOPE) {
            className += " env-edge";
        } else {
            className += " lfo-edge";
        }
    }
    if (macro) {
        className += " macro-edge";
    }
    if (selected) {
        className += " selected";
    }
    if (rack.getEffectType() === OSCILLATOR && modulator === undefined) {
        className += " oscillator-edge";

        if (!edge.operator.synth) {
            className += " fm-output-edge";
        }
    }
    if (isHovered) {
        className += " hovered";
    }


    const onMouseOver = useCallback(() => {
        setIsHovered(true);
    }, [setIsHovered]);

    const onMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, [setIsHovered]);

    const onMouseDown= useCallback(() => {
        if (modulator) {
            setSelectedEdge({
                modulator,
                edge
            });
        } else {
            setSelectedEdge(edge);
        }
    }, [setSelectedEdge, edge, modulator]);


    if (x2 === undefined || y2 === undefined) {
        return [];
    }

    if (!modulator && !macro &&
        edge.gainParameter === undefined &&
        edge.modulation === undefined) {
        return (
            []
        );
    }

    let opacity = 1;
    if (playing && rack.getEffectType() === MIDI_FX && !isHovered && !selected) {
        if (triggerCount === 0) {
            opacity = 0.4;
        }
    } else if (!macro && !modulator && !selected) {
        opacity = Math.max(0.2, edge.getWeight(rack));
    }


    let startAndEndPoints = [];
    if (selected) {
        startAndEndPoints = [
            (<circle
               strokeWidth={1}
               onMouseDown={startDraggingStart}
               stroke={'red'}
               cx={x1} cy={y1} r={3} fill="white"
             />),
            (<circle
               strokeWidth={1}
               onMouseDown={startDraggingEnd}
               stroke={'red'}
               cx={x2} cy={y2}
               r={3}
               fill="white"/>),
        ];
    }

    let d = Math.sqrt(dist(x1,y1,x2,y2)) * window.dist;
    let yDistance = Math.abs(y2-y1);
    let xd = Math.abs(x2 - x1);
    let xDistance = Math.sqrt(xd);
    if (x2 - x1 < 0) {
        xDistance *= -1;
    }
    if (xd < 20 && rack.getEffectType() !== OSCILLATOR) {
        xDistance += (20 - xd);
    }
    return (
        <g>
           <Arrow
             className={className}
              onMouseOver={onMouseOver}
              onMouseLeave={onMouseLeave}
              tension={window.tension}
              onMouseDown={onMouseDown}
              pointerLength= {6}
              opacity={opacity}
              pointerWidth={4}
        points={[x1, y1, x1 + window.x*xDistance, y1+d, x2 - window.x*xDistance, y2-d, x2, y2]}
        strokeWidth={2} />
            <Arrow
              onMouseOver={onMouseOver}
              onMouseLeave={onMouseLeave}
              onMouseDown={onMouseDown}
              pointerLength= {6}
              pointerWidth={4}
              tension={window.tension}
             className={className}
              opacity={0}
              points={[x1, y1, x1 + window.x*xDistance, y1 + d, x2-window.x*xDistance, y2 - d, x2, y2]}
              strokeWidth={8}
            />
          {startAndEndPoints}
        </g>
    );
}

function getParameterXPosition(parameter, rack) {
    if (parameter === undefined) {
        return undefined;
    } 
    let device = parameter.context && (parameter.context.macro || parameter.context.subDevice);
    if (device === undefined ||
        device.parameters === undefined) {
        return 0;
    }
    let parameters = device.parameters
        .filter(x => x.automatable);

    if (rack.getEffectType() === OSCILLATOR) {
        parameters = [device.harmonicityParameter, device.modulationIndexParameter];
    }
    let indexOf = parameters.indexOf(parameter);
    if (indexOf === -1) {
        return undefined;
    }
    return device.x + 8 + indexOf*8;
}

function getModulatorPositions(rack, modulator, edge) {
    if (modulator === undefined) {
        return {
            x1: undefined,
            y1: undefined,
            x2: undefined,
            y2: undefined,
        };
    }

    let number = modulator.getType() === ENVELOPE ?
        4 + modulator.number : modulator.number;
    let x1 = 8 + (number) / (rack.modulators.length + 1) * WIDTHS[rack.getEffectType()];
    let y1 = NODE_HEIGHTS[rack.getEffectType()];
    let x2 = getParameterXPosition(edge, rack);

    let device = edge.context && (edge.context.macro || edge.context.subDevice);
    let y2 = device === undefined ? undefined  : device.y;
    return {
        x1, y1, x2, y2
    };
}

function getMacroPositions(rack, macro, edge) {
    if (macro === undefined) {
        return {
            x1: undefined,
            y1: undefined,
            x2: undefined,
            y2: undefined,
        };
    }

    let x1 = macro.x + 8;
    let y1 = macro.y + NODE_HEIGHTS[rack.getEffectType()];
    let x2 = getParameterXPosition(edge, rack);
    let y2 = edge.context && edge.context.subDevice.y;
    return {
        x1, y1, x2, y2
    };
}

const mapStateToProps = (state, props) => {
    if (props.modulator) {
        let device = props.edge.context && (props.edge.context.macro || props.edge.context.subDevice);
        if (device) {
            return {
                x2: device.x,
                y2: device.y,
            };
        } else {
            return {
                x2: 0,
                y2: 0
            };
        }
    }
    if (props.macro && props.edge.parameter.context) {
        return {
            x1: props.macro.x,
            y1: props.macro.y,
            x2: props.edge.parameter.context.subDevice.x,
            y2: props.edge.parameter.context.subDevice.y,
        };
    }
    if (!props.effect) {
        return {x1: 0, y1: 0, x2: 0, y2: 0};
    }
    return {
        x1: props.effect.x,
        y1: props.effect.y,
        x2: props.edge.effect.x,
        y2: props.edge.effect.y,
    };
};

export default connect(mapStateToProps, null)(Edge);
